import {
    Button,
    ButtonColor,
    Container,
    ContainerSize,
    Gap,
    Heading,
    Popover,
    PopoverPlacement,
    Stack,
    Text,
} from "@rebootui";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

const PopoverPage = () => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Popover
            </Heading>

            <Text className="mb-4">
                Rich overlay with optional <Text el="strong">title</Text> and required <Text el="strong">content</Text>.
                Visibility is controlled by <Text el="strong">trigger</Text> (click, hover, or focus); press{" "}
                <Text el="kbd">Escape</Text> to dismiss when open.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Container
            </Heading>
            <Text className="mb-4">
                Default <Text el="strong">container=&quot;body&quot;</Text> portals the panel; use{" "}
                <Text el="strong">inline</Text> with a <Text el="strong">position-relative</Text> parent when you want the
                popover in the layout tree (better for hover).
            </Text>

            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Body (default)
            </Heading>
            <CodeSnippet>
                <CodePreview>
                    <Popover title="Portal popover" content="Rendered under document.body by default.">
                        <Button color={ButtonColor.Primary}>Default container</Button>
                    </Popover>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {PopoverPortalCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Inline
            </Heading>
            <Text className="mb-4">
                Basic example with inline positioning.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <div className="position-relative">
                        <Popover
                            container="inline"
                            title="Popover Title"
                            content="This is the popover body content with more detailed information."
                        >
                            <Button color={ButtonColor.Primary}>Click me</Button>
                        </Popover>
                    </div>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {PopoverBasicCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Placement
            </Heading>
            <Text className="mb-4">
                Set <Text el="strong">PopoverPlacement</Text> on the component.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <div className="position-relative">
                            <Popover
                                container="inline"
                                title="Top Popover"
                                content="This popover appears on top."
                                placement={PopoverPlacement.Top}
                            >
                                <Button color={ButtonColor.Primary}>Popover on top</Button>
                            </Popover>
                        </div>
                        <div className="position-relative">
                            <Popover
                                container="inline"
                                title="End Popover"
                                content="This popover appears on the end (right)."
                                placement={PopoverPlacement.End}
                            >
                                <Button color={ButtonColor.Primary}>Popover on end</Button>
                            </Popover>
                        </div>
                        <div className="position-relative">
                            <Popover
                                container="inline"
                                title="Bottom Popover"
                                content="This popover appears on bottom."
                                placement={PopoverPlacement.Bottom}
                            >
                                <Button color={ButtonColor.Primary}>Popover on bottom</Button>
                            </Popover>
                        </div>
                        <div className="position-relative">
                            <Popover
                                container="inline"
                                title="Start Popover"
                                content="This popover appears on start (left)."
                                placement={PopoverPlacement.Start}
                            >
                                <Button color={ButtonColor.Primary}>Popover on start</Button>
                            </Popover>
                        </div>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {PopoverPlacementCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Triggers
            </Heading>
            <Text className="mb-4">
                <Text el="strong">click</Text> (default), <Text el="strong">hover</Text>, or <Text el="strong">focus</Text>.
                For hover with a portaled panel, prefer <Text el="strong">inline</Text> so the pointer can reach the popover.
            </Text>

            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Hover
            </Heading>
            <CodeSnippet>
                <CodePreview>
                    <div className="position-relative">
                        <Popover
                            container="inline"
                            title="Hover Popover"
                            content="This popover appears when you hover over the button."
                            trigger="hover"
                        >
                            <Button color={ButtonColor.Secondary}>Hover me</Button>
                        </Popover>
                    </div>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {PopoverHoverCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Focus
            </Heading>
            <CodeSnippet>
                <CodePreview>
                    <Popover
                        title="Focus Popover"
                        content="This popover appears when the button receives focus. Try tabbing to it!"
                        trigger="focus"
                    >
                        <Button color={ButtonColor.Success}>Tab to me</Button>
                    </Popover>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {PopoverFocusCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Without title
            </Heading>
            <Text className="mb-4">
                <Text el="strong">title</Text> is optional.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Popover content="This popover has no title, only body content.">
                        <Button color={ButtonColor.Info}>Popover without title</Button>
                    </Popover>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {PopoverNoTitleCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    );
};

export default PopoverPage;

const PopoverPortalCode = `<Popover title="Portal popover" content="Rendered under document.body by default.">
    <Button color={ButtonColor.Primary}>Default container</Button>
</Popover>`;

const PopoverBasicCode = `<div className="position-relative">
    <Popover container="inline" title="Popover Title" content="This is the popover body content with more detailed information.">
        <Button color={ButtonColor.Primary}>Click me</Button>
    </Popover>
</div>`;

const PopoverPlacementCode = `<Stack gap={Gap.Gap2}>
    <div className="position-relative">
        <Popover container="inline" title="Top Popover" content="This popover appears on top." placement={PopoverPlacement.Top}>
            <Button color={ButtonColor.Primary}>Popover on top</Button>
        </Popover>
    </div>
    <div className="position-relative">
        <Popover container="inline" title="End Popover" content="This popover appears on the end (right)." placement={PopoverPlacement.End}>
            <Button color={ButtonColor.Primary}>Popover on end</Button>
        </Popover>
    </div>
    <div className="position-relative">
        <Popover container="inline" title="Bottom Popover" content="This popover appears on bottom." placement={PopoverPlacement.Bottom}>
            <Button color={ButtonColor.Primary}>Popover on bottom</Button>
        </Popover>
    </div>
    <div className="position-relative">
        <Popover container="inline" title="Start Popover" content="This popover appears on start (left)." placement={PopoverPlacement.Start}>
            <Button color={ButtonColor.Primary}>Popover on start</Button>
        </Popover>
    </div>
</Stack>`;

const PopoverHoverCode = `<div className="position-relative">
    <Popover
        container="inline"
        title="Hover Popover"
        content="This popover appears when you hover over the button."
        trigger="hover"
    >
        <Button color={ButtonColor.Secondary}>Hover me</Button>
    </Popover>
</div>`;

const PopoverFocusCode = `<Popover
    title="Focus Popover"
    content="This popover appears when the button receives focus. Try tabbing to it!"
    trigger="focus"
>
    <Button color={ButtonColor.Success}>Tab to me</Button>
</Popover>`;

const PopoverNoTitleCode = `<Popover content="This popover has no title, only body content.">
    <Button color={ButtonColor.Info}>Popover without title</Button>
</Popover>`;
